/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { useUIStateStore } from "@/store/uiStateStore";

import useGetColours from "../helpers/getColours";
import RebrandButtonLink from "./RebrandButtonLink";
import Spacer from "./Spacer";

function defaultOnBlur() {}

function DoubleMultipleInput(props) {
  const {
    value,
    onChange,
    valueKey1,
    valueKey2,
    valueKey3,
    reset,
    placeholder1,
    placeholder2,
    placeholder3,
    area,
    required,
    addAnotherText,
    type,
    error,
    onBlur = defaultOnBlur,
    className,
    maxLength,
    items,
    setItems,
  } = props;
  const [focus, setFocus] = useState(null);

  const { highlightColour2 } = useGetColours();
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { t } = useTranslation();

  useEffect(() => {
    const isDefault = !items || (items.length === 1 && items[0][valueKey1] === "" && items[0][valueKey2] === "" && items[0][valueKey3] === "");
    if (isDefault && value) {
      if (value.length > 0) {
        const itemArray = value.map(v => ({ ...v, id: uuidv4() }));
        setItems(itemArray);
      }
      else {
        const initialItem = [
          {
            id: uuidv4(),
            [valueKey1]: "",
            [valueKey2]: "",
            [valueKey3]: "",
          },
        ];
        setItems(initialItem);
      }
    }
  }, [value, valueKey1, valueKey2, valueKey3]);

  // Use a ref to track previous reset value to avoid unnecessary updates
  const prevResetRef = React.useRef(reset);

  useEffect(() => {
    // Only reset when the reset value changes and is truthy
    if (reset && reset !== prevResetRef.current) {
      if (value && value.length > 0) {
        const itemArray = value.map(v => ({ ...v, id: uuidv4() }));
        setItems(itemArray);
        console.log("Reset items from API value:", itemArray);
      }
      else {
        const initialItem = [
          {
            id: uuidv4(),
            [valueKey1]: "",
            [valueKey2]: "",
            [valueKey3]: "",
          },
        ];
        setItems(initialItem);
        console.log("Reset items as empty:", initialItem);
      }
      prevResetRef.current = reset;
    }
  }, [reset, value, valueKey1, valueKey2, valueKey3, setItems]);

  const updateOnChange = (newItems) => {
    // Create a deep copy to avoid reference issues
    const copy = newItems.map(({ id, ...rest }) => rest);

    // Check if the value has actually changed before calling onChange
    // This prevents unnecessary rerenders
    if (JSON.stringify(copy) !== JSON.stringify(value || [])) {
      setItems(newItems);
      onChange(copy);
    }
  };

  const onChangeItem = ({ id, val, valueKey }) => {
    // Find the item to update
    const item = items.find(item => item.id === id);

    // Skip update if value hasn't changed
    if (item && item[valueKey] === val)
      return;

    // Create new array with updated item
    const newItems = items.map(item =>
      item.id === id ? { ...item, [valueKey]: val } : item,
    );
    updateOnChange(newItems);
  };

  const remove = (id) => {
    const newItems = items.filter(q => q.id !== id);
    updateOnChange(newItems);
  };

  const addAnother = () => {
    const newItems = [
      ...items,
      {
        id: uuidv4(),
        [valueKey1]: "",
        [valueKey2]: "",
        [valueKey3]: "",
      },
    ];
    updateOnChange(newItems);
  };

  return (
    <div className={`${className}`}>
      {items?.map((v, i) => (
        <React.Fragment key={`item${v.id}`}>
          <div className="font-semibold bg-transparent border rounded-lg font-sentric-alt">
            <div>
              <label
                className={[
                  `pointer-events-none flex text-sm items-center pb-0.5 ${
                    isTunecore
                      ? "text-white opacity-70"
                      : "font-sentric-alt font-semibold text-sentric-rebrand-grey-text"
                  }`,
                ]}
                htmlFor={`item${v.id}`}
              >
                {placeholder1}
                {items.length > 1 && (
                  <>
                    {" "}
                    {i + 1}
                  </>
                )}
              </label>
              <div
                id={`txt${area}${placeholder1}`}
                className={`mb-2 relative bg-white block w-full rounded-md border-0 ${
                  required ? "required-star" : ""
                }`}
              >
                <input
                  className="w-full p-2 pl-3 pr-3 text-sm font-semibold border-0 rounded-lg focus:outline-none focus:ring-2"
                  id={`item${v.id}`}
                  name={`item${v.id}`}
                  maxLength={maxLength}
                  value={v[valueKey1] || ""}
                  type={type || "text"}
                  onFocus={(e) => {
                    e.target.style.boxShadow = `0 0 0 2px ${highlightColour2}`;
                    setFocus(v.id);
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = "none";
                    setFocus(null);
                    onBlur();
                  }}
                  onMouseEnter={e =>
                    (e.target.style.boxShadow = `0 0 0 2px ${highlightColour2}`)}
                  onMouseLeave={e =>
                    (e.target.style.boxShadow = focus === v.id ? `0 0 0 2px ${highlightColour2}` : "none")}
                  onChange={e =>
                    onChangeItem({
                      id: v.id,
                      val: e.target.value,
                      valueKey: valueKey1,
                    })}
                />
              </div>
            </div>
            <div>
              <label
                className={[
                  `pointer-events-none flex text-sm items-center pb-0.5 ${
                    isTunecore
                      ? "text-white opacity-70"
                      : "font-sentric-alt font-semibold text-sentric-rebrand-grey-text"
                  }`,
                ]}
                htmlFor={`item${v.id}`}
              >
                {placeholder2}
                {items.length > 1 && (
                  <>
                    {" "}
                    {i + 1}
                  </>
                )}
              </label>
              <div
                id={`txt${area}${placeholder2}`}
                className={`mb-2 relative bg-white block w-full rounded-md border-0 ${
                  required ? "required-star" : ""
                }`}
              >
                <input
                  className={[
                    "rounded-lg border-0 focus:outline-none focus:ring-2 w-full text-sm font-semibold p-2 pl-3 pr-3",
                    v[valueKey2] ? "" : "",
                  ].join(" ")}
                  id={`item2${v.id}`}
                  name={`item2${v.id}`}
                  maxLength={maxLength}
                  value={v[valueKey2] || ""}
                  type={type || "text"}
                  onFocus={(e) => {
                    e.target.style.boxShadow = `0 0 0 2px ${highlightColour2}`;
                    setFocus(v.id);
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = "none";
                    setFocus(null);
                    onBlur();
                  }}
                  onMouseEnter={e =>
                    (e.target.style.boxShadow = `0 0 0 2px ${highlightColour2}`)}
                  onMouseLeave={e =>
                    (e.target.style.boxShadow = focus === v.id ? `0 0 0 2px ${highlightColour2}` : "none")}
                  onChange={e =>
                    onChangeItem({
                      id: v.id,
                      val: e.target.value,
                      valueKey: valueKey2,
                    })}
                />
              </div>
            </div>
            <div>
              <label
                className={[
                  `pointer-events-none flex text-sm items-center pb-0.5 ${
                    isTunecore
                      ? "text-white opacity-70"
                      : "font-sentric-alt font-semibold text-sentric-rebrand-grey-text"
                  }`,
                ]}
                htmlFor={`item${v.id}`}
              >
                {placeholder3}
                {items.length > 1 && (
                  <>
                    {" "}
                    {i + 1}
                  </>
                )}
              </label>
              <div
                id={`txt${area}${placeholder3}`}
                className={`mb-2 relative bg-white block w-full rounded-md border-0 ${
                  required ? "required-star" : ""
                }`}
              >
                <input
                  className={[
                    "rounded-lg border-0 focus:outline-none focus:ring-2 w-full text-sm font-semibold p-2 pl-3 pr-3",
                    v[valueKey3] ? "" : "",
                  ].join(" ")}
                  id={`item${v.id}`}
                  name={`item${v.id}`}
                  maxLength={maxLength}
                  value={v[valueKey3] || ""}
                  type={type || "text"}
                  onFocus={(e) => {
                    e.target.style.boxShadow = `0 0 0 2px ${highlightColour2}`;
                    setFocus(v.id);
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = "none";
                    setFocus(null);
                    onBlur();
                  }}
                  onMouseEnter={e =>
                    (e.target.style.boxShadow = `0 0 0 2px ${highlightColour2}`)}
                  onMouseLeave={e =>
                    (e.target.style.boxShadow = focus === v.id ? `0 0 0 2px ${highlightColour2}` : "none")}
                  onChange={e =>
                    onChangeItem({
                      id: v.id,
                      val: e.target.value,
                      valueKey: valueKey3,
                    })}
                />
              </div>
            </div>
          </div>
          {items.length > 1 && (
            <div className="flex justify-end text-sm">
              <button
                className="items-center h-4 text-black underline transition-all duration-150 ease-linear hover:scale-105"
                type="button"
                onClick={() => remove(v.id)}
              >
                <span className="ml-2">{t("common:remove")}</span>
              </button>
            </div>
          )}
          <Spacer thin />
        </React.Fragment>
      ))}
      {!!error && (
        <p className="inline-block w-4/5 p-1 pl-3 pr-3 mt-2 text-sm text-white bg-red-700 border border-white rounded-lg">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end w-full h-4 mt-4">
        <RebrandButtonLink
          plusIcon
          onClick={addAnother}
          id={`btn${area}AddAnother`}
        >
          {addAnotherText}
        </RebrandButtonLink>
      </div>
    </div>
  );
}

export default DoubleMultipleInput;
